import React from 'react';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pushEvent } from '../utils/analytics';

// WhatsApp Business number (approved): +91 99993 07127
const WA_CONSUMER_URL =
  'https://wa.me/919999307127?text=' +
  encodeURIComponent("Hi NoBelly team, I'd like to know more about NoBelly Bier.");

const WhatsAppIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.52 3.48A11.85 11.85 0 0012.06 0C5.53 0 .24 5.29.24 11.82c0 2.08.54 4.11 1.57 5.9L0 24l6.44-1.7a11.84 11.84 0 005.62 1.43h.01c6.53 0 11.82-5.29 11.82-11.82 0-3.16-1.23-6.13-3.37-8.43zM12.07 21.5h-.01a9.7 9.7 0 01-4.94-1.36l-.36-.21-3.82 1.01 1.02-3.72-.24-.38a9.66 9.66 0 01-1.5-5.16c0-5.35 4.36-9.7 9.72-9.7 2.6 0 5.03 1.01 6.87 2.84a9.63 9.63 0 012.85 6.87c-.01 5.35-4.37 9.71-9.72 9.71zm5.32-7.28c-.29-.14-1.72-.85-1.98-.95-.27-.1-.46-.14-.66.14-.19.29-.75.94-.92 1.14-.17.19-.34.22-.63.07-.29-.14-1.23-.45-2.34-1.44-.87-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.44.13-.58.13-.13.29-.34.44-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.14-.66-1.59-.9-2.17-.24-.57-.48-.49-.66-.5-.17-.01-.36-.01-.55-.01-.19 0-.51.07-.77.36-.27.29-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.19 3 .14.19 2.05 3.13 4.97 4.39.7.3 1.24.48 1.66.61.7.22 1.33.19 1.84.11.56-.08 1.72-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34z"/>
  </svg>
);

const Footer = () => {
  return (
    <footer className="dark-footer">
      <div className="footer-content">
        {/* Compliance Warnings */}
        <div className="footer-warnings">
          <p className="warning-large">CONSUMPTION OF ALCOHOL IS INJURIOUS TO HEALTH</p>
          <p className="warning-large">BE SAFE DON'T DRINK AND DRIVE</p>
          <p className="warning-medium">DRINK RESPONSIBLY</p>
        </div>

        <div className="footer-separator"></div>

        {/* Company Information */}
        <div className="footer-grid">
          <div className="footer-column">
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/tue77akt_Logo.webp" 
              alt="No-Belly Bier" 
              className="footer-logo"
            />
            <p className="footer-tagline">Clean brewed. Consciously crafted.</p>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Explore</h3>
            <Link to="/our-beer" className="footer-link">Our Beer</Link>
            <Link to="/find-us" className="footer-link">Find Us</Link>
            <Link to="/our-story" className="footer-link">Our Story</Link>
            <Link to="/events" className="footer-link">Events</Link>
            <Link to="/horeca" className="footer-link">For Trade</Link>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Legal</h3>
            <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            <Link to="/terms" className="footer-link">Terms of Use</Link>
            <Link to="/responsible-drinking" className="footer-link">Responsible Drinking</Link>
            <Link to="/legal" className="footer-link">Compliance</Link>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Contact</h3>
            <a
              href="tel:+919999307127"
              className="footer-contact-item"
              onClick={() => pushEvent('consumer_contact_click', { button_location: 'footer_phone' })}
            >
              <Phone size={18} />
              <span>+91 9999307127</span>
            </a>
            <a
              href="mailto:contact@nobelly.in"
              className="footer-contact-item"
              onClick={() => pushEvent('consumer_contact_click', { button_location: 'footer_email' })}
            >
              <Mail size={18} />
              <span>contact@nobelly.in</span>
            </a>
            <a
              href={WA_CONSUMER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-whatsapp-cta"
              aria-label="Chat with NoBelly on WhatsApp"
              data-testid="footer-whatsapp-cta"
              onClick={() => pushEvent('whatsapp_click', {
                button_location: 'footer_contact',
                contact_type: 'consumer',
                page_path: (typeof window !== 'undefined' ? window.location.pathname : '/'),
              })}
            >
              <WhatsAppIcon size={16} /> CHAT ON WHATSAPP
            </a>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Follow Us</h3>
            <div className="social-row">
              <a
                href="https://instagram.com/nobellybier"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Instagram - @nobellybier"
                data-testid="footer-insta-nobellybier"
                onClick={() => pushEvent('instagram_click', { button_location: 'footer_brand' })}
              >
                <Instagram size={22} />
              </a>
              <div className="social-row-text">
                <span className="social-handle">@nobellybier</span>
                <span className="social-sub">Brand</span>
              </div>
            </div>
            <div className="social-row">
              <a
                href="https://instagram.com/coupleofbiers"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Instagram - @coupleofbiers"
                data-testid="footer-insta-coupleofbiers"
                onClick={() => pushEvent('instagram_click', { button_location: 'footer_founders' })}
              >
                <Instagram size={22} />
              </a>
              <div className="social-row-text">
                <span className="social-handle">@coupleofbiers</span>
                <span className="social-sub">Founders</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-separator"></div>

        {/* Trademark & Legal */}
        <div className="footer-legal">
          <p className="legal-text">
            <strong>Legal Entity:</strong> Hopfenzauber No-Belly Bier Pvt. Ltd.
          </p>
          <p className="legal-text">
            <MapPin size={16} className="inline-icon" />
            Innov8 Orchid Center, 3rd Floor, Golf Course Road, Sector 53, Gurgaon, Haryana 122002
          </p>
        </div>

        <div className="footer-separator"></div>

        <div className="footer-bottom">
          <p>© 2026 No-Belly Bier — Hopfenzauber No-Belly Bier Pvt. Ltd. All rights reserved.</p>
          <p className="footer-compliance">NO ONLINE SALE OR DELIVERY OF ALCOHOL. INFORMATIONAL WEBSITE ONLY.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
